/**
 * HCS Organization Service
 *
 * API client for HCS organization management endpoints.
 * Part of UC-016: HCS Organization Management
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';

/**
 * List all organizations with optional filtering
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (active, inactive, pending, suspended)
 * @param {string} options.search - Search by org_id or org_name
 * @param {number} options.limit - Max results (default 50)
 * @param {number} options.offset - Pagination offset
 * @returns {Promise<{organizations: Array, total: number}>}
 */
export const listOrganizations = async ({ status, search, limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (search) params.append('search', search);
  params.append('limit', limit);
  params.append('offset', offset);

  const response = await fetch(`${API_URL}/hcs/organizations?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to list organizations');
  }

  return response.json();
};

/**
 * Get a single organization by ID
 * @param {string} orgId - Organization ID (e.g., HCS0001)
 * @returns {Promise<Object>}
 */
export const getOrganization = async (orgId) => {
  const response = await fetch(`${API_URL}/hcs/organizations/${orgId}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to get organization');
  }

  return response.json();
};

/**
 * Create a new organization
 * @param {Object} orgData - Organization data
 * @param {string} orgData.org_id - Organization ID (e.g., HCS0001)
 * @param {string} orgData.org_name - Display name
 * @param {string} orgData.database_name - Snowflake database name
 * @param {string} orgData.warehouse_name - Snowflake warehouse name
 * @param {string} orgData.role_name - Snowflake role name
 * @param {string} [orgData.azure_container] - Azure blob container
 * @param {string} [orgData.notes] - Notes
 * @param {string} [adminEmail] - Admin email for audit
 * @returns {Promise<Object>}
 */
export const createOrganization = async (orgData, adminEmail) => {
  const params = new URLSearchParams();
  if (adminEmail) params.append('admin_email', adminEmail);

  const response = await fetch(`${API_URL}/hcs/organizations?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orgData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to create organization');
  }

  return response.json();
};

/**
 * Update an organization
 * @param {string} orgId - Organization ID
 * @param {Object} updates - Fields to update
 * @param {string} [adminEmail] - Admin email for audit
 * @returns {Promise<Object>}
 */
export const updateOrganization = async (orgId, updates, adminEmail) => {
  const params = new URLSearchParams();
  if (adminEmail) params.append('admin_email', adminEmail);

  const response = await fetch(`${API_URL}/hcs/organizations/${orgId}?${params.toString()}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to update organization');
  }

  return response.json();
};

/**
 * Deactivate (soft delete) an organization
 * @param {string} orgId - Organization ID
 * @param {string} [adminEmail] - Admin email for audit
 * @returns {Promise<Object>}
 */
export const deactivateOrganization = async (orgId, adminEmail) => {
  const params = new URLSearchParams();
  if (adminEmail) params.append('admin_email', adminEmail);

  const response = await fetch(`${API_URL}/hcs/organizations/${orgId}?${params.toString()}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to deactivate organization');
  }

  return response.json();
};

/**
 * List users assigned to an organization
 * @param {string} orgId - Organization ID
 * @param {Object} options - Query options
 * @returns {Promise<{assignments: Array, total: number}>}
 */
export const listOrganizationUsers = async (orgId, { limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  params.append('offset', offset);

  const response = await fetch(`${API_URL}/hcs/organizations/${orgId}/users?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to list organization users');
  }

  return response.json();
};

/**
 * Assign a user to an organization
 * @param {string} orgId - Organization ID
 * @param {Object} assignment - Assignment data
 * @param {string} assignment.user_id - User ID
 * @param {boolean} [assignment.is_primary] - Set as primary org
 * @param {string} [assignment.access_level] - Access level (standard, admin, readonly)
 * @param {string} [adminEmail] - Admin email for audit
 * @returns {Promise<Object>}
 */
export const assignUserToOrganization = async (orgId, assignment, adminEmail) => {
  const params = new URLSearchParams();
  if (adminEmail) params.append('admin_email', adminEmail);

  const response = await fetch(`${API_URL}/hcs/organizations/${orgId}/users?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(assignment),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to assign user to organization');
  }

  return response.json();
};

/**
 * Remove a user from an organization
 * @param {string} orgId - Organization ID
 * @param {string} userId - User ID
 * @param {string} [adminEmail] - Admin email for audit
 * @returns {Promise<Object>}
 */
export const removeUserFromOrganization = async (orgId, userId, adminEmail) => {
  const params = new URLSearchParams();
  if (adminEmail) params.append('admin_email', adminEmail);

  const response = await fetch(`${API_URL}/hcs/organizations/${orgId}/users/${userId}?${params.toString()}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to remove user from organization');
  }

  return response.json();
};

/**
 * List organizations for a user
 * @param {string} userId - User ID
 * @returns {Promise<{user_id: string, organizations: Array, total: number}>}
 */
export const listUserOrganizations = async (userId) => {
  const response = await fetch(`${API_URL}/hcs/users/${userId}/organizations`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to list user organizations');
  }

  return response.json();
};

/**
 * Set an organization as the user's primary
 * @param {string} userId - User ID
 * @param {string} orgId - Organization ID
 * @param {string} [adminEmail] - Admin email for audit
 * @returns {Promise<Object>}
 */
export const setUserPrimaryOrganization = async (userId, orgId, adminEmail) => {
  const params = new URLSearchParams();
  if (adminEmail) params.append('admin_email', adminEmail);

  const response = await fetch(`${API_URL}/hcs/users/${userId}/organizations/${orgId}/primary?${params.toString()}`, {
    method: 'PUT',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to set primary organization');
  }

  return response.json();
};

/**
 * Validate an organization's Snowflake resources
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>}
 */
export const validateOrganization = async (orgId) => {
  const response = await fetch(`${API_URL}/hcs/organizations/${orgId}/validate`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to validate organization');
  }

  return response.json();
};

export default {
  listOrganizations,
  getOrganization,
  createOrganization,
  updateOrganization,
  deactivateOrganization,
  listOrganizationUsers,
  assignUserToOrganization,
  removeUserFromOrganization,
  listUserOrganizations,
  setUserPrimaryOrganization,
  validateOrganization,
};
